import React, { useEffect, useState, createElement } from 'react';
import { Clock, Download, Search, User, FileText, Mail, Tag, Users, Settings, Send, ChevronDown, ChevronRight, ChevronLeft, RefreshCw, Filter, X } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { auditLogApi, AuditLogRecord, CreatorFilter } from '../services/creatorApi';
import { toast } from 'sonner';
const actionColors: Record<string, {
  bg: string;
  text: string;
}> = {
  created: {
    bg: 'bg-green-100',
    text: 'text-green-800'
  },
  updated: {
    bg: 'bg-blue-100',
    text: 'text-blue-800'
  },
  deleted: {
    bg: 'bg-red-100',
    text: 'text-red-800'
  },
  status_changed: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-800'
  },
  duplicated: {
    bg: 'bg-purple-100',
    text: 'text-purple-800'
  },
  archived: {
    bg: 'bg-gray-100',
    text: 'text-gray-800'
  },
  restored: {
    bg: 'bg-teal-100',
    text: 'text-teal-800'
  }
};
const entityIcons: Record<string, any> = {
  form: FileText,
  template: Mail,
  newsletter: Mail,
  campaign: Send,
  user: User,
  tag: Tag,
  form_config: Settings,
  workspace: Users,
  subscription: Users,
  submission: FileText,
  contact: Users,
  role: Settings,
  audience: Users
};
// Hardcoded options for server-side filtering
const ACTION_OPTIONS = [{
  value: 'created',
  label: 'Created'
}, {
  value: 'updated',
  label: 'Updated'
}, {
  value: 'deleted',
  label: 'Deleted'
}, {
  value: 'status_changed',
  label: 'Status Changed'
}, {
  value: 'duplicated',
  label: 'Duplicated'
}, {
  value: 'archived',
  label: 'Archived'
}, {
  value: 'restored',
  label: 'Restored'
}];
const ENTITY_OPTIONS = [{
  value: 'form',
  label: 'Form'
}, {
  value: 'template',
  label: 'Template'
}, {
  value: 'newsletter',
  label: 'Newsletter'
}, {
  value: 'campaign',
  label: 'Campaign'
}, {
  value: 'user',
  label: 'User'
}, {
  value: 'tag',
  label: 'Tag'
}, {
  value: 'form_config',
  label: 'Form Config'
}, {
  value: 'workspace',
  label: 'Workspace'
}, {
  value: 'subscription',
  label: 'Subscription'
}, {
  value: 'submission',
  label: 'Submission'
}, {
  value: 'contact',
  label: 'Contact'
}, {
  value: 'role',
  label: 'Role'
}, {
  value: 'audience',
  label: 'Audience'
}];
/** Convert unix timestamp (seconds) to Date */
function parseUnixTimestamp(ts?: string): Date | null {
  if (!ts) return null;
  const num = Number(ts);
  if (isNaN(num)) return null;
  return new Date(num * 1000);
}
/** Format a unix timestamp for display */
function formatTimestamp(ts?: string): {
  date: string;
  time: string;
} {
  const d = parseUnixTimestamp(ts);
  if (!d) return {
    date: '',
    time: ''
  };
  return {
    date: d.toLocaleDateString(),
    time: d.toLocaleTimeString()
  };
}
const PAGE_SIZE = 200;
export function AuditLog() {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const [auditLogs, setAuditLogs] = useState<AuditLogRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Pending filter state (UI inputs, not yet applied)
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAction, setFilterAction] = useState<string>('all');
  const [filterEntity, setFilterEntity] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  // Active (committed) filter state — what's actually sent to the server
  const [activeSearch, setActiveSearch] = useState('');
  const [activeAction, setActiveAction] = useState<string>('all');
  const [activeEntity, setActiveEntity] = useState<string>('all');
  const [activeDateFrom, setActiveDateFrom] = useState('');
  const [activeDateTo, setActiveDateTo] = useState('');
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const showPagination = totalCount > PAGE_SIZE;
  // Server-side stat counts
  const [createdCount, setCreatedCount] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [deletedCount, setDeletedCount] = useState(0);
  const [statusChangedCount, setStatusChangedCount] = useState(0);
  // UI state
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const buildScopeFilters = (): CreatorFilter[] => {
    const filters: CreatorFilter[] = [];
    filters.push({
      field: 'Team',
      value: currentWorkspace.name
    });
    if (currentSubRegion) {
      filters.push({
        field: 'Region',
        value: currentSubRegion
      });
    }
    return filters;
  };
  const buildAllFilters = (action: string, entity: string, search: string, dFrom: string, dTo: string): CreatorFilter[] => {
    const filters: CreatorFilter[] = [...buildScopeFilters()];
    if (action !== 'all') {
      filters.push({
        field: 'Action_Type',
        value: action
      });
    }
    if (entity !== 'all') {
      filters.push({
        field: 'Entity_Type',
        value: entity
      });
    }
    if (dFrom) {
      const fromDate = new Date(dFrom);
      fromDate.setHours(0, 0, 0, 0);
      filters.push({
        field: 'created',
        value: String(Math.floor(fromDate.getTime() / 1000)),
        op: '>='
      });
    }
    if (dTo) {
      const toDate = new Date(dTo);
      toDate.setHours(23, 59, 59, 999);
      filters.push({
        field: 'created',
        value: String(Math.floor(toDate.getTime() / 1000)),
        op: '<='
      });
    }
    if (search.trim()) {
      filters.push({
        fields: ['Entity_Name', 'Summary', 'User_Name'],
        value: search.trim()
      });
    }
    return filters;
  };
  const loadAuditLogs = async (page: number = 1, action: string = activeAction, entity: string = activeEntity, search: string = activeSearch, dFrom: string = activeDateFrom, dTo: string = activeDateTo) => {
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * PAGE_SIZE;
      const dataFilters = buildAllFilters(action, entity, search, dFrom, dTo);
      // Counts use all filters EXCEPT Action_Type (each count adds its own)
      const countBaseFilters = buildAllFilters('all', entity, search, dFrom, dTo);
      // Fetch data + stat counts in parallel
      const [dataResult, createdResult, updatedResult, deletedResult, statusResult] = await Promise.all([auditLogApi.list({
        filters: dataFilters,
        offset: offset > 0 ? offset : undefined
      }), auditLogApi.list({
        filters: [...countBaseFilters, {
          field: 'Action_Type',
          value: 'created'
        }],
        only_count: true
      }), auditLogApi.list({
        filters: [...countBaseFilters, {
          field: 'Action_Type',
          value: 'updated'
        }],
        only_count: true
      }), auditLogApi.list({
        filters: [...countBaseFilters, {
          field: 'Action_Type',
          value: 'deleted'
        }],
        only_count: true
      }), auditLogApi.list({
        filters: [...countBaseFilters, {
          field: 'Action_Type',
          value: 'status_changed'
        }],
        only_count: true
      })]);
      const logs = dataResult.data;
      // Sort by created timestamp descending (newest first)
      logs.sort((a, b) => {
        const tsA = Number(a.created) || 0;
        const tsB = Number(b.created) || 0;
        return tsB - tsA;
      });
      setAuditLogs(logs);
      setTotalCount(dataResult.total_count);
      setCreatedCount(createdResult.total_count);
      setUpdatedCount(updatedResult.total_count);
      setDeletedCount(deletedResult.total_count);
      setStatusChangedCount(statusResult.total_count);
      setCurrentPage(page);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      setError('Failed to load audit logs. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  // Initial load + reload on scope change
  useEffect(() => {
    loadAuditLogs(1, activeAction, activeEntity, activeSearch, activeDateFrom, activeDateTo);
  }, [currentWorkspace.name, currentSubRegion]);
  const handleApplyFilters = () => {
    setActiveSearch(searchQuery.trim());
    setActiveAction(filterAction);
    setActiveEntity(filterEntity);
    setActiveDateFrom(dateFrom);
    setActiveDateTo(dateTo);
    loadAuditLogs(1, filterAction, filterEntity, searchQuery.trim(), dateFrom, dateTo);
  };
  const handleClearFilters = () => {
    setSearchQuery('');
    setFilterAction('all');
    setFilterEntity('all');
    setDateFrom('');
    setDateTo('');
    setActiveSearch('');
    setActiveAction('all');
    setActiveEntity('all');
    setActiveDateFrom('');
    setActiveDateTo('');
    loadAuditLogs(1, 'all', 'all', '', '', '');
  };
  const hasActiveFilters = activeSearch !== '' || activeAction !== 'all' || activeEntity !== 'all' || activeDateFrom !== '' || activeDateTo !== '';
  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    loadAuditLogs(page);
  };
  const toggleExpanded = (id: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedEvents(newExpanded);
  };
  const handleExportCSV = () => {
    if (auditLogs.length === 0) return;
    const headers = ['Date', 'Action', 'Entity Type', 'Entity Name', 'Summary', 'User', 'Email', 'Role', 'IP Address'];
    const csvContent = [headers.join(','), ...auditLogs.map(e => [`"${e.created ? new Date(Number(e.created) * 1000).toISOString() : ''}"`, `"${e.Action_Type || ''}"`, `"${e.Entity_Type || ''}"`, `"${(e.Entity_Name || '').replace(/"/g, '""')}"`, `"${(e.Summary || '').replace(/"/g, '""')}"`, `"${(e.User_Name || '').replace(/"/g, '""')}"`, `"${(e.User_Email || '').replace(/"/g, '""')}"`, `"${(e.User_Role || '').replace(/"/g, '""')}"`, `"${e.IP_Address || ''}"`].join(','))].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `audit_logs_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const parseJSON = (str?: string) => {
    if (!str) return null;
    try {
      return JSON.parse(str);
    } catch {
      return null;
    }
  };
  const allCount = createdCount + updatedCount + deletedCount + statusChangedCount;
  const paginationPanel = showPagination ? <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3" data-id="element-2677">
      <div className="text-sm text-gray-600" data-id="element-2678">
        Showing {(currentPage - 1) * PAGE_SIZE + 1}–
        {Math.min(currentPage * PAGE_SIZE, totalCount)} of{' '}
        {totalCount.toLocaleString()} events
      </div>
      <div className="flex items-center gap-2" data-id="element-2679">
        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1 || loading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2680">
          <ChevronLeft className="h-4 w-4" data-id="element-2681" />
          Previous
        </button>
        <span className="text-sm text-gray-700 px-2" data-id="element-2682">
          Page {currentPage} of {totalPages}
        </span>
        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages || loading} className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2683">
          Next
          <ChevronRight className="h-4 w-4" data-id="element-2684" />
        </button>
      </div>
    </div> : null;
  return <div className="space-y-6" data-id="element-2685">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-2686">
        <div className="min-w-0" data-id="element-2687">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2688">Audit Log</h1>
          <p className="mt-1 text-sm text-gray-500" data-id="element-2689">
            Complete history of all changes made in your workspace.
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-2690">
          <button onClick={() => loadAuditLogs(currentPage)} disabled={loading} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-2691">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} data-id="element-2692" />
            <span className="hidden sm:inline" data-id="element-2693">Refresh</span>
          </button>
          <button onClick={handleExportCSV} disabled={auditLogs.length === 0 || loading} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-2694">
            <Download className="h-4 w-4" data-id="element-2695" />
            <span data-id="element-2696">Export CSV</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4" data-id="element-2697">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-2698">
          <div className="relative flex-1 min-w-0" data-id="element-2699">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-2700" />
            <input type="text" placeholder="Search by entity name, user, summary..." className="pl-9 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => {
            if (e.key === 'Enter') handleApplyFilters();
          }} data-id="element-2701" />
          </div>
          <div className="flex gap-2 flex-shrink-0 flex-wrap sm:flex-nowrap" data-id="element-2702">
            <select className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 flex-shrink-0" value={filterAction} onChange={e => setFilterAction(e.target.value)} data-id="element-2703">
              <option value="all" data-id="element-2704">All Actions</option>
              {ACTION_OPTIONS.map(opt => <option key={opt.value} value={opt.value} data-id="element-2705">
                  {opt.label}
                </option>)}
            </select>
            <select className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 flex-shrink-0" value={filterEntity} onChange={e => setFilterEntity(e.target.value)} data-id="element-2706">
              <option value="all" data-id="element-2707">All Entities</option>
              {ENTITY_OPTIONS.map(opt => <option key={opt.value} value={opt.value} data-id="element-2708">
                  {opt.label}
                </option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-id="element-2709">
          <div className="flex items-center gap-2 text-sm" data-id="element-2710">
            <span className="text-gray-500" data-id="element-2711">Date Range:</span>
            <input type="date" className="px-2 py-1 border border-gray-300 rounded text-sm text-gray-700" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-id="element-2712" />
            <span className="text-gray-400" data-id="element-2713">to</span>
            <input type="date" className="px-2 py-1 border border-gray-300 rounded text-sm text-gray-700" value={dateTo} onChange={e => setDateTo(e.target.value)} data-id="element-2714" />
          </div>
          <div className="flex items-center gap-2 sm:ml-auto" data-id="element-2715">
            {hasActiveFilters && <button onClick={handleClearFilters} disabled={loading} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50" data-id="element-2716">
                <X className="h-3.5 w-3.5" data-id="element-2717" />
                Clear
              </button>}
            <button onClick={handleApplyFilters} disabled={loading} className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-2718">
              <Filter className="h-3.5 w-3.5" data-id="element-2719" />
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-2720">
        <div className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-2721">
          <p className="text-2xl font-semibold text-gray-900" data-id="element-2722">
            {loading ? '-' : allCount.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-2723">Total Events</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-2724">
          <p className="text-2xl font-semibold text-green-600" data-id="element-2725">
            {loading ? '-' : createdCount.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-2726">Created</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-2727">
          <p className="text-2xl font-semibold text-blue-600" data-id="element-2728">
            {loading ? '-' : updatedCount.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-2729">Updated</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3" data-id="element-2730">
          <p className="text-2xl font-semibold text-red-600" data-id="element-2731">
            {loading ? '-' : deletedCount.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-2732">Deleted</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3 col-span-2 sm:col-span-1" data-id="element-2733">
          <p className="text-2xl font-semibold text-yellow-600" data-id="element-2734">
            {loading ? '-' : statusChangedCount.toLocaleString()}
          </p>
          <p className="text-xs text-gray-500 truncate" data-id="element-2735">Status Changed</p>
        </div>
      </div>

      {/* Error State */}
      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md" data-id="element-2736">
          {error}
        </div>}

      {/* Top Pagination */}
      {paginationPanel}

      {/* Events List */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2737">
        {loading ? <div className="divide-y divide-gray-200" data-id="element-2738">
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="p-4 flex items-start gap-4 animate-pulse" data-id="element-2739">
                <div className="h-10 w-10 bg-gray-200 rounded-full flex-shrink-0" data-id="element-2740"></div>
                <div className="flex-1 space-y-2" data-id="element-2741">
                  <div className="h-4 bg-gray-200 rounded w-1/3" data-id="element-2742"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/4" data-id="element-2743"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-24" data-id="element-2744"></div>
              </div>)}
          </div> : auditLogs.length === 0 ? <div className="p-12 text-center" data-id="element-2745">
            <Clock className="mx-auto h-12 w-12 text-gray-400" data-id="element-2746" />
            <h3 className="mt-2 text-sm font-semibold text-gray-900" data-id="element-2747">
              No events found
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-2748">
              {hasActiveFilters ? 'Try adjusting your filters.' : 'No audit events have been recorded yet.'}
            </p>
          </div> : <div className="divide-y divide-gray-200" data-id="element-2749">
            {auditLogs.map(event => {
          const EntityIcon = entityIcons[event.Entity_Type] || FileText;
          const actionStyle = actionColors[event.Action_Type] || actionColors.updated;
          const isExpanded = expandedEvents.has(event.ID);
          const changes = parseJSON(event.Changes_JSON);
          const metadata = parseJSON(event.Metadata_JSON);
          const hasDetails = changes && changes.length > 0 || metadata;
          return <div key={event.ID} className="hover:bg-gray-50" data-id="element-2750">
                  <div className={`p-4 flex items-start gap-3 sm:gap-4 ${hasDetails ? 'cursor-pointer' : ''}`} onClick={() => hasDetails && toggleExpanded(event.ID)} data-id="element-2751">
                    {/* Icon */}
                    <div className={`flex-shrink-0 h-8 w-8 sm:h-10 sm:w-10 rounded-full flex items-center justify-center ${actionStyle.bg}`} data-id="element-2752">
                      <EntityIcon className={`h-4 w-4 sm:h-5 sm:w-5 ${actionStyle.text}`} data-id="element-2753" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0" data-id="element-2754">
                      <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-2755">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${actionStyle.bg} ${actionStyle.text}`} data-id="element-2756">
                          {(event.Action_Type || 'unknown').replace('_', ' ')}
                        </span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 whitespace-nowrap" data-id="element-2757">
                          {(event.Entity_Type || 'unknown').replace('_', ' ')}
                        </span>
                        <span className="text-sm font-medium text-gray-900 truncate" data-id="element-2758">
                          {event.Entity_Name}
                        </span>
                      </div>

                      {event.Summary && <p className="text-sm text-gray-600 mb-1" data-id="element-2759">
                          {event.Summary}
                        </p>}

                      <div className="flex items-center gap-2 text-xs text-gray-500 flex-wrap" data-id="element-2760">
                        <span className="flex items-center gap-1 whitespace-nowrap" data-id="element-2761">
                          <User className="h-3 w-3 flex-shrink-0" data-id="element-2762" />
                          <span className="font-medium text-gray-700" data-id="element-2763">
                            {event.User_Name || 'System'}
                          </span>
                        </span>
                        {event.User_Email && <>
                            <span className="hidden sm:inline" data-id="element-2764">•</span>
                            <span className="hidden sm:inline truncate" data-id="element-2765">
                              {event.User_Email}
                            </span>
                          </>}
                        {event.User_Role && <>
                            <span className="hidden sm:inline" data-id="element-2766">•</span>
                            <span className="hidden sm:inline px-1.5 py-0.5 bg-gray-100 rounded text-[10px]" data-id="element-2767">
                              {event.User_Role}
                            </span>
                          </>}
                      </div>
                    </div>

                    {/* Timestamp & Expand */}
                    <div className="flex items-center gap-2 text-right flex-shrink-0" data-id="element-2768">
                      {(() => {
                  const ts = formatTimestamp(event.created);
                  return <>
                            <div className="hidden sm:block" data-id="element-2769">
                              <div className="text-sm text-gray-900 whitespace-nowrap" data-id="element-2770">
                                {ts.date}
                              </div>
                              <div className="text-xs text-gray-500" data-id="element-2771">
                                {ts.time}
                              </div>
                            </div>
                            <div className="sm:hidden text-xs text-gray-500 whitespace-nowrap" data-id="element-2772">
                              {ts.date}
                            </div>
                          </>;
                })()}
                      {hasDetails && <div className="text-gray-400 flex-shrink-0 ml-2" data-id="element-2773">
                          {isExpanded ? <ChevronDown className="h-4 w-4" data-id="element-2774" /> : <ChevronRight className="h-4 w-4" data-id="element-2775" />}
                        </div>}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && hasDetails && <div className="px-4 pb-4 ml-11 sm:ml-14 border-l-2 border-gray-100" data-id="element-2776">
                      {changes && changes.length > 0 && <div className="bg-gray-50 rounded-md p-3 space-y-2 border border-gray-200" data-id="element-2777">
                          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-2778">
                            Changes
                          </p>
                          <div className="overflow-x-auto" data-id="element-2779">
                            <table className="min-w-full text-sm text-left" data-id="element-2780">
                              <thead className="text-xs text-gray-500 border-b border-gray-200" data-id="element-2781">
                                <tr data-id="element-2782">
                                  <th className="pb-2 font-medium" data-id="element-2783">Field</th>
                                  <th className="pb-2 font-medium" data-id="element-2784">
                                    Old Value
                                  </th>
                                  <th className="pb-2 font-medium" data-id="element-2785">
                                    New Value
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100" data-id="element-2786">
                                {changes.map((change: any, idx: number) => <tr key={idx} data-id="element-2787">
                                    <td className="py-2 pr-4 font-medium text-gray-700 align-top" data-id="element-2788">
                                      {change.field}
                                    </td>
                                    <td className="py-2 pr-4 text-red-600 align-top break-all max-w-xs" data-id="element-2789">
                                      {change.oldValue === null || change.oldValue === undefined || change.oldValue === '' ? <span className="text-gray-400 italic" data-id="element-2790">
                                          (empty)
                                        </span> : typeof change.oldValue === 'object' ? JSON.stringify(change.oldValue) : String(change.oldValue)}
                                    </td>
                                    <td className="py-2 text-green-600 align-top break-all max-w-xs" data-id="element-2791">
                                      {change.newValue === null || change.newValue === undefined || change.newValue === '' ? <span className="text-gray-400 italic" data-id="element-2792">
                                          (empty)
                                        </span> : typeof change.newValue === 'object' ? JSON.stringify(change.newValue) : String(change.newValue)}
                                    </td>
                                  </tr>)}
                              </tbody>
                            </table>
                          </div>
                        </div>}

                      {metadata && Object.keys(metadata).length > 0 && <div className="bg-gray-50 rounded-md p-3 mt-3 border border-gray-200" data-id="element-2793">
                          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2" data-id="element-2794">
                            Additional Info
                          </p>
                          <pre className="text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap break-all" data-id="element-2795">
                            {JSON.stringify(metadata, null, 2)}
                          </pre>
                        </div>}

                      {event.IP_Address && <div className="mt-3 text-xs text-gray-500 flex items-center gap-1" data-id="element-2796">
                          <span className="font-medium" data-id="element-2797">IP Address:</span>{' '}
                          {event.IP_Address}
                        </div>}
                    </div>}
                </div>;
        })}
          </div>}
      </div>

      {/* Bottom Pagination */}
      {paginationPanel}
    </div>;
}